import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import { convertCuboidToAdjustedVisibilityBoxProps } from '@/utils/geometry';
import { useCuboidState } from './useCuboidState';
import { CuboidBase } from '@/modules/cuboids/types';
import * as cuboidState from '@/components/Analysis/modules/cuboids/state';
import { MAX_RGB_VALUE } from '@/constants';

type IsolateCuboidOptions = {
  focus?: boolean;
};
export const useIsolateCuboid = () => {
  const setCurrentVisibilityBox = useSetAtom(state.currentVisibilityBox);
  const setCameraTarget = useSetAtom(state.cameraTarget);
  const setIsIsolatedWorkscope = useSetAtom(cuboidState.isIsolatedWorkscope);
  const setAreSphericalsVisible = useSetAtom(state.areSphericalsVisible);
  const [blendOpacity, setBlendingOpacity] = useAtom(state.blendOpacity);
  const selectedStructureDefectsLegends = useAtomValue(state.selectedStructureDefectsLegends);

  const userHasEnabledDefects = useMemo(
    () => selectedStructureDefectsLegends?.some((defect) => defect.isEnabled) ?? false,
    [selectedStructureDefectsLegends]
  );

  const isolateCuboid = useCallback(
    (cuboid: CuboidBase, options?: IsolateCuboidOptions) => {
      setIsIsolatedWorkscope(true);
      setAreSphericalsVisible(false);
      if (userHasEnabledDefects && blendOpacity === 1) {
        setBlendingOpacity(MAX_RGB_VALUE);
      }

      const visibilityBox = convertCuboidToAdjustedVisibilityBoxProps(cuboid);

      setCurrentVisibilityBox(visibilityBox);

      if (options?.focus) {
        const boxCenter = visibilityBox.position;
        setCameraTarget((previous) => ({
          ...previous,
          lookAt: boxCenter,
          position: [boxCenter[0] + 10, boxCenter[1], boxCenter[2] + 50],
          zoom: 10,
        }));
      }
    },
    [
      setIsIsolatedWorkscope,
      setAreSphericalsVisible,
      userHasEnabledDefects,
      blendOpacity,
      setCurrentVisibilityBox,
      setBlendingOpacity,
      setCameraTarget,
    ]
  );

  return {
    isolateCuboid,
  };
};

export const useIsolateCuboidEffect = () => {
  const cuboidState = useCuboidState();

  const currentCuboid = useAtomValue(cuboidState.currentCuboid);
  const [isAddingWorkscope, setIsAddingWorkscope] = useAtom(cuboidState.isAddingWorkscope);
  const isCuboidEditorEnabled = useAtomValue(cuboidState.isCuboidEditorEnabled);

  const { isolateCuboid } = useIsolateCuboid();

  useEffect(() => {
    // Trying to capture the event where
    // 1- The user is adding a workscope cuboid
    // 2 - The have just finished adding the cuboid
    // Since 3D viewer does not give a callback on cuboid done/resize/move etc
    // This effect becomes necessary
    if (isAddingWorkscope && currentCuboid?.cuboidData && !isCuboidEditorEnabled) {
      isolateCuboid(currentCuboid);
      setIsAddingWorkscope(false);
    }
  }, [
    currentCuboid,
    isAddingWorkscope,
    isCuboidEditorEnabled,
    isolateCuboid,
    setIsAddingWorkscope,
  ]);
};
